/**
 * Admin Template Configuration page.
 *
 * Lets administrators tune fonts, colors, spacings, section order/visibility
 * and per-language section titles. Changes apply globally to every CV export
 * (Word + PDF) from the next request onwards.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Eye, Save, RotateCcw, GripVertical, Palette, Type, Ruler, ListOrdered, Sparkles, Layout as LayoutIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe font whitelist — must match backend ALLOWED_FONTS_* lists
const PDF_FONTS = ['Helvetica', 'Times-Roman', 'Courier'];
const WORD_FONTS = ['IBM Plex Sans', 'Calibri', 'Arial', 'Times New Roman', 'Verdana', 'Georgia'];

const SECTION_LANGS = [
  { code: 'pt', label: 'PT' },
  { code: 'en', label: 'EN' },
  { code: 'fr', label: 'FR' },
  { code: 'de', label: 'DE' },
  { code: 'es', label: 'ES' },
];

const SECTION_LABEL = {
  pt: {
    summary: 'Resumo Profissional', main_competencies: 'Competências Principais',
    experiences: 'Experiência Profissional', education: 'Formação e Certificações',
    languages: 'Idiomas',
  },
  en: {
    summary: 'Professional Summary', main_competencies: 'Main Competencies',
    experiences: 'Professional Experience', education: 'Education & Certifications',
    languages: 'Languages',
  },
};

function SortableSection({ section, language, onToggle, onTitleChange }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: section.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const label = (SECTION_LABEL[language] || SECTION_LABEL.pt)[section.key] || section.key;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`section-row-${section.key}`}
      className="flex flex-col gap-3 p-4 bg-white rounded-lg border border-gray-200 hover:border-[#1E3C3C]/30"
    >
      <div className="flex items-center gap-3">
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#1E3C3C]"
          data-testid={`drag-handle-${section.key}`}
          aria-label={`Reordenar ${label}`}
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <p className="font-semibold text-[#0f172a]">{label}</p>
          <p className="text-xs text-[#64748b]">chave: <code>{section.key}</code></p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor={`vis-${section.key}`} className="text-xs text-[#64748b]">
            {language === 'pt' ? 'Visível' : 'Visible'}
          </Label>
          <Switch
            id={`vis-${section.key}`}
            checked={section.visible}
            onCheckedChange={(v) => onToggle(section.key, v)}
            data-testid={`section-visible-${section.key}`}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
        {SECTION_LANGS.map(l => (
          <div key={l.code}>
            <Label className="text-xs text-[#64748b]">{l.label}</Label>
            <Input
              value={section[`title_${l.code}`] || ''}
              onChange={(e) => onTitleChange(section.key, l.code, e.target.value)}
              className="h-9 text-sm"
              data-testid={`section-title-${section.key}-${l.code}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ColorRow({ field, label, value, onChange }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-1">
        <p className="text-sm font-medium text-[#0f172a]">{label}</p>
        <p className="text-xs text-[#64748b]">{field}</p>
      </div>
      <Input
        type="color"
        value={`#${(value || '000000').replace(/^#/, '')}`}
        onChange={(e) => onChange(field, e.target.value.replace(/^#/, '').toUpperCase())}
        className="h-10 w-16 p-1"
        data-testid={`color-${field}`}
      />
      <Input
        value={(value || '').toUpperCase()}
        onChange={(e) => onChange(field, e.target.value.replace(/^#/, '').toUpperCase())}
        className="h-10 w-28 font-mono text-sm uppercase"
      />
    </div>
  );
}

function SizeRow({ field, label, value, onChange, min = 6, max = 36, step = 0.5 }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="flex-1">
        <p className="text-sm font-medium text-[#0f172a]">{label}</p>
        <p className="text-xs text-[#64748b]">{field}</p>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(field, parseFloat(e.target.value))}
        className="flex-1 max-w-[200px]"
        data-testid={`size-range-${field}`}
      />
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(field, parseFloat(e.target.value))}
        step={step}
        min={min}
        max={max}
        className="h-10 w-20 text-sm"
        data-testid={`size-input-${field}`}
      />
      <span className="text-xs text-[#64748b] w-6">pt</span>
    </div>
  );
}

export default function AdminTemplate() {
  const { token, isAdmin, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isPT = language === 'pt';

  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const previewWinRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin()) {
      toast.error(isPT ? 'Apenas administradores' : 'Admin only');
      navigate('/cvs');
      return;
    }
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading]);

  const loadConfig = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCfg(res.data);
    } catch (e) {
      toast.error(isPT ? 'Erro a carregar configuração' : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!cfg) return;
    try {
      setSaving(true);
      const payload = {
        fonts: cfg.fonts,
        colors: cfg.colors,
        spacing: cfg.spacing,
        sections: cfg.sections,
        header: cfg.header,
      };
      const res = await axios.put(`${API}/admin/template`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCfg(res.data);
      toast.success(isPT ? 'Configuração guardada' : 'Configuration saved');
    } catch (e) {
      const msg = e.response?.data?.detail || (isPT ? 'Erro ao guardar' : 'Save error');
      toast.error(typeof msg === 'string' ? msg : (isPT ? 'Erro ao guardar' : 'Save error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(isPT ? 'Repor todos os valores predefinidos?' : 'Reset all to defaults?')) return;
    try {
      setSaving(true);
      const res = await axios.post(`${API}/admin/template/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCfg(res.data);
      toast.success(isPT ? 'Predefinidos repostos' : 'Defaults restored');
    } catch (e) {
      toast.error(isPT ? 'Erro' : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    // Save first so the preview uses what the admin sees.
    await handleSave();
    // Get the first CV to render a sample preview.
    try {
      setPreviewing(true);
      const listRes = await axios.get(`${API}/cvs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const sample = listRes.data?.[0];
      if (!sample) {
        toast.info(isPT ? 'Crie um CV primeiro para pré-visualizar' : 'Create a CV first to preview');
        return;
      }
      const pdfRes = await axios.get(`${API}/cvs/${sample.id}/preview/pt`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      if (previewWinRef.current && !previewWinRef.current.closed) {
        previewWinRef.current.close();
      }
      previewWinRef.current = window.open(url, '_blank');
    } catch (e) {
      toast.error(isPT ? 'Erro a pré-visualizar' : 'Preview error');
    } finally {
      setPreviewing(false);
    }
  };

  const updateColor = (field, value) =>
    setCfg(c => ({ ...c, colors: { ...c.colors, [field]: value } }));
  const updateSize = (field, value) =>
    setCfg(c => ({ ...c, fonts: { ...c.fonts, sizes: { ...c.fonts.sizes, [field]: value } } }));
  const updateSpacing = (field, value) =>
    setCfg(c => ({ ...c, spacing: { ...c.spacing, [field]: value } }));
  const updateFontFamily = (field, value) =>
    setCfg(c => ({ ...c, fonts: { ...c.fonts, [field]: value } }));
  const updateHeader = (field, value) =>
    setCfg(c => ({ ...c, header: { ...(c.header || {}), [field]: value } }));

  const onSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCfg(c => {
      const oldIndex = c.sections.findIndex(s => s.key === active.id);
      const newIndex = c.sections.findIndex(s => s.key === over.id);
      return { ...c, sections: arrayMove(c.sections, oldIndex, newIndex) };
    });
  };

  const toggleSectionVisible = (key, visible) =>
    setCfg(c => ({
      ...c,
      sections: c.sections.map(s => s.key === key ? { ...s, visible } : s),
    }));

  const setSectionTitle = (key, lang, value) =>
    setCfg(c => ({
      ...c,
      sections: c.sections.map(s => s.key === key ? { ...s, [`title_${lang}`]: value } : s),
    }));

  if (loading || !cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8fafc]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] py-8" data-testid="admin-template-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-[#0f172a]">
              {isPT ? 'Configuração de Template' : 'Template Configuration'}
            </h1>
            <p className="text-sm text-[#64748b] mt-1">
              {isPT
                ? 'Afina a apresentação corporativa dos CVs (Word e PDF). Aplica-se a todas as exportações futuras.'
                : 'Tune the corporate look of every exported CV (Word + PDF). Applies to all future exports.'}
            </p>
            {cfg.updated_by && (
              <p className="text-xs text-[#94a3b8] mt-1">
                {isPT ? 'Última atualização por' : 'Last updated by'} <strong>{cfg.updated_by}</strong>
                {cfg.updated_at && ` · ${new Date(cfg.updated_at).toLocaleString()}`}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={saving}
              data-testid="reset-template-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              {isPT ? 'Repor predefinidos' : 'Reset defaults'}
            </Button>
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={saving || previewing}
              data-testid="preview-template-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              {isPT ? 'Pré-visualizar PDF' : 'Preview PDF'}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#1E3C3C] hover:bg-[#0F766E]"
              data-testid="save-template-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? (isPT ? 'A guardar…' : 'Saving…') : (isPT ? 'Guardar' : 'Save')}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="sections" className="space-y-4">
          <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full bg-white">
            <TabsTrigger value="sections" data-testid="tab-sections">
              <ListOrdered className="w-4 h-4 mr-2" />
              {isPT ? 'Secções' : 'Sections'}
            </TabsTrigger>
            <TabsTrigger value="header" data-testid="tab-header">
              <LayoutIcon className="w-4 h-4 mr-2" />
              {isPT ? 'Cabeçalho' : 'Header'}
            </TabsTrigger>
            <TabsTrigger value="typography" data-testid="tab-typography">
              <Type className="w-4 h-4 mr-2" />
              {isPT ? 'Tipografia' : 'Typography'}
            </TabsTrigger>
            <TabsTrigger value="colors" data-testid="tab-colors">
              <Palette className="w-4 h-4 mr-2" />
              {isPT ? 'Cores' : 'Colors'}
            </TabsTrigger>
            <TabsTrigger value="spacing" data-testid="tab-spacing">
              <Ruler className="w-4 h-4 mr-2" />
              {isPT ? 'Espaçamentos' : 'Spacing'}
            </TabsTrigger>
          </TabsList>

          {/* SECTIONS */}
          <TabsContent value="sections">
            <Card>
              <CardContent className="pt-6 space-y-3">
                <p className="text-sm text-[#64748b] mb-3">
                  <Sparkles className="w-4 h-4 inline mr-1 text-[#C9A84C]" />
                  {isPT
                    ? 'Arraste para reordenar, alterne a visibilidade e renomeie os títulos por idioma.'
                    : 'Drag to reorder, toggle visibility and rename titles per language.'}
                </p>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onSectionDragEnd}
                >
                  <SortableContext
                    items={cfg.sections.map(s => s.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3" data-testid="sections-list">
                      {cfg.sections.map(s => (
                        <SortableSection
                          key={s.key}
                          section={s}
                          language={language}
                          onToggle={toggleSectionVisible}
                          onTitleChange={setSectionTitle}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </CardContent>
            </Card>
          </TabsContent>

          {/* HEADER VISIBILITY */}
          <TabsContent value="header">
            <Card>
              <CardContent className="pt-6 space-y-1">
                <p className="text-sm text-[#64748b] mb-4">
                  {isPT
                    ? 'Controle quais elementos aparecem na faixa verde-escura no topo do CV. Útil para CVs mais discretos ou anónimos.'
                    : 'Choose which elements appear inside the dark green band at the top of each CV. Useful for anonymised or stripped-down profiles.'}
                </p>
                {[
                  { f: 'show_logo', l: isPT ? 'Logótipo Evolutt' : 'Evolutt logo',
                    d: isPT ? 'Imagem branca no canto superior esquerdo' : 'White image in the top-left corner' },
                  { f: 'show_score', l: 'Score Evolutt',
                    d: isPT ? 'Label "Score Evolutt" e o número grande à direita' : '"Score Evolutt" label and the big number on the right' },
                  { f: 'show_years_exp', l: isPT ? 'Anos de experiência' : 'Years of experience',
                    d: isPT ? 'Linha inferior, lado esquerdo' : 'Bottom line, left side' },
                  { f: 'show_availability', l: isPT ? 'Disponibilidade' : 'Availability',
                    d: isPT ? 'Mostrada ao lado dos anos de experiência' : 'Shown next to years of experience' },
                  { f: 'show_function', l: isPT ? 'Função (junto ao nome)' : 'Function (next to name)',
                    d: isPT ? 'Ex: "João Silva - Consultor IT"' : 'e.g. "John Smith - IT Consultant"' },
                ].map(({ f, l, d }) => (
                  <div key={f} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-[#0f172a]">{l}</p>
                      <p className="text-xs text-[#64748b]">{d}</p>
                    </div>
                    <Switch
                      checked={!!(cfg.header && cfg.header[f])}
                      onCheckedChange={(v) => updateHeader(f, v)}
                      data-testid={`header-${f}`}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TYPOGRAPHY */}
          <TabsContent value="typography">
            <Card>
              <CardContent className="pt-6 divide-y divide-gray-100">
                {/* Font family selectors */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-4">
                  <div>
                    <Label className="text-sm font-medium text-[#0f172a]">
                      {isPT ? 'Família de fonte (PDF)' : 'Font family (PDF)'}
                    </Label>
                    <p className="text-xs text-[#64748b] mb-2">family_pdf</p>
                    <select
                      value={cfg.fonts.family_pdf || 'Helvetica'}
                      onChange={(e) => updateFontFamily('family_pdf', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
                      data-testid="font-family-pdf"
                    >
                      {PDF_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-[#0f172a]">
                      {isPT ? 'Família de fonte (Word)' : 'Font family (Word)'}
                    </Label>
                    <p className="text-xs text-[#64748b] mb-2">family_word</p>
                    <select
                      value={cfg.fonts.family_word || 'IBM Plex Sans'}
                      onChange={(e) => updateFontFamily('family_word', e.target.value)}
                      className="w-full h-10 px-3 rounded-md border border-gray-200 bg-white text-sm"
                      data-testid="font-family-word"
                    >
                      {WORD_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                  </div>
                </div>
                {[
                  { f: 'name', l: isPT ? 'Nome do candidato (cabeçalho)' : 'Candidate name (header)' },
                  { f: 'function', l: isPT ? 'Função (cabeçalho)' : 'Function (header)' },
                  { f: 'info', l: isPT ? 'Anos exp. · Disponibilidade' : 'Years exp · Availability' },
                  { f: 'score_label', l: 'Score Evolutt label' },
                  { f: 'score_value', l: isPT ? 'Score % (número grande)' : 'Score % (big number)' },
                  { f: 'heading', l: isPT ? 'Títulos de secção (PDF)' : 'Section titles (PDF)' },
                  { f: 'heading_word', l: isPT ? 'Títulos de secção (Word)' : 'Section titles (Word)' },
                  { f: 'body', l: isPT ? 'Texto corrido (PDF)' : 'Body text (PDF)' },
                  { f: 'body_word', l: isPT ? 'Texto corrido (Word)' : 'Body text (Word)' },
                  { f: 'title', l: isPT ? 'Cargo dentro das experiências' : 'Job title in experiences' },
                  { f: 'bullet', l: isPT ? 'Bullets das experiências' : 'Experience bullets' },
                  { f: 'dot', l: isPT ? 'Pontos da avaliação' : 'Rating dots' },
                ].map(({ f, l }) => (
                  <SizeRow
                    key={f}
                    field={f}
                    label={l}
                    value={cfg.fonts.sizes[f]}
                    onChange={updateSize}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* COLORS */}
          <TabsContent value="colors">
            <Card>
              <CardContent className="pt-6 divide-y divide-gray-100">
                {[
                  { f: 'brand', l: isPT ? 'Verde Evolutt (cabeçalho)' : 'Evolutt green (header)' },
                  { f: 'gold', l: isPT ? 'Linhas douradas' : 'Gold separator lines' },
                  { f: 'heading', l: isPT ? 'Títulos de secção' : 'Section headings' },
                  { f: 'body', l: isPT ? 'Texto corrido' : 'Body text' },
                  { f: 'name', l: isPT ? 'Nomes / Cargos' : 'Names / Roles' },
                  { f: 'grey', l: isPT ? 'Datas e secundário' : 'Dates / secondary' },
                  { f: 'dot_filled', l: isPT ? 'Ponto preenchido (rating)' : 'Filled rating dot' },
                  { f: 'dot_empty', l: isPT ? 'Ponto vazio (rating)' : 'Empty rating dot' },
                  { f: 'score_interview', l: 'Score Entrevista' },
                  { f: 'score_technical', l: 'Score Teste Técnico' },
                ].map(({ f, l }) => (
                  <ColorRow
                    key={f}
                    field={f}
                    label={l}
                    value={cfg.colors[f]}
                    onChange={updateColor}
                  />
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* SPACING */}
          <TabsContent value="spacing">
            <Card>
              <CardContent className="pt-6 divide-y divide-gray-100">
                <div className="flex items-center gap-3 py-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0f172a]">
                      {isPT ? 'Altura do cabeçalho' : 'Header height'}
                    </p>
                    <p className="text-xs text-[#64748b]">header_height_cm</p>
                  </div>
                  <input
                    type="range" min="2" max="7" step="0.1"
                    value={cfg.spacing.header_height_cm}
                    onChange={(e) => updateSpacing('header_height_cm', parseFloat(e.target.value))}
                    className="flex-1 max-w-[200px]"
                    data-testid="spacing-header-range"
                  />
                  <Input
                    type="number" step="0.1" min="2" max="7"
                    value={cfg.spacing.header_height_cm}
                    onChange={(e) => updateSpacing('header_height_cm', parseFloat(e.target.value))}
                    className="h-10 w-20"
                    data-testid="spacing-header-input"
                  />
                  <span className="text-xs text-[#64748b] w-6">cm</span>
                </div>
                <div className="flex items-center gap-3 py-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0f172a]">
                      {isPT ? 'Margens laterais da página' : 'Page side margins'}
                    </p>
                    <p className="text-xs text-[#64748b]">page_margin_cm</p>
                  </div>
                  <input
                    type="range" min="1" max="3" step="0.1"
                    value={cfg.spacing.page_margin_cm}
                    onChange={(e) => updateSpacing('page_margin_cm', parseFloat(e.target.value))}
                    className="flex-1 max-w-[200px]"
                    data-testid="spacing-margin-range"
                  />
                  <Input
                    type="number" step="0.1" min="1" max="3"
                    value={cfg.spacing.page_margin_cm}
                    onChange={(e) => updateSpacing('page_margin_cm', parseFloat(e.target.value))}
                    className="h-10 w-20"
                  />
                  <span className="text-xs text-[#64748b] w-6">cm</span>
                </div>
                <div className="flex items-center gap-3 py-2">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#0f172a]">
                      {isPT ? 'Entrelinha (corpo)' : 'Body line spacing'}
                    </p>
                    <p className="text-xs text-[#64748b]">line_spacing</p>
                  </div>
                  <input
                    type="range" min="1" max="2" step="0.05"
                    value={cfg.spacing.line_spacing}
                    onChange={(e) => updateSpacing('line_spacing', parseFloat(e.target.value))}
                    className="flex-1 max-w-[200px]"
                    data-testid="spacing-line-range"
                  />
                  <Input
                    type="number" step="0.05" min="1" max="2"
                    value={cfg.spacing.line_spacing}
                    onChange={(e) => updateSpacing('line_spacing', parseFloat(e.target.value))}
                    className="h-10 w-20"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
